import { useCallback, useEffect } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  setResearchAssistantIsProcessing,
  setProgressStep,
  updateAVProgress,
  setWorkflowId,
} from '@verantos-dev/app-state-research-assistant';
import {
  parseProgressStepType,
  type AVProgressAction,
  type AVProgressItem,
  type AVProgressStatus,
  type WorkflowProgressRestore,
} from '@verantos-dev/types-common';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';

/**
 * Handles the `workflow_progress_restore` event emitted by the backend when a
 * client subscribes to a room that has an active (in-flight) workflow.
 *
 * This restores the progress UI after a page reload or new client connection,
 * and re-arms the timeout guard timer with the remaining deadline.
 *
 * Listeners are always registered — the backend gates whether to emit this
 * event behind the `analytical_variables_in_cohort_builder` feature flag.
 *
 * @param startTimer - Callback from useWorkflowTimeoutGuard to re-arm the safety timer.
 *
 * TODO: cleanup with analytical_variables_in_cohort_builder ff — remove this comment
 */
export const useWorkflowProgressRestoreListener = (
  startTimer: (workflowId: string, timeoutAt: string, timeoutSeconds: number) => void
) => {
  const dispatch = useAppDispatch();
  const { socket: currentSocket } = useResearchAssistantSocketContext();

  const handler = useCallback(
    (data: WorkflowProgressRestore) => {
      console.info('[WorkflowProgressRestore] Received:', {
        workflow_id: data?.workflow_id,
        has_user_prompt: !!data?.user_prompt,
        status: data?.status,
      });
      if (!data?.workflow_id) return;

      // Restore processing state — this locks the UI (input, send, cohort buttons)
      dispatch(setResearchAssistantIsProcessing({ transactionId: data.workflow_id }));
      dispatch(setWorkflowId(data.workflow_id));

      // The user message is already in history (persisted immediately when the
      // workflow started), so we do NOT re-add it here to avoid duplicates.

      // Restore current step
      if (data.current_step) {
        const stepType = parseProgressStepType(data.current_step.step);
        if (stepType) {
          dispatch(setProgressStep({ step: stepType, message: data.current_step.message }));
        }
      }

      // Restore AV progress
      if (data.av_progress) {
        for (const av of data.av_progress) {
          const item: AVProgressItem = {
            avIndex: av.av_index,
            name: av.name,
            action: av.action as AVProgressAction,
            status: av.status as AVProgressStatus,
            retryMessage: av.retry_message,
          };
          dispatch(updateAVProgress(item));
        }
      }

      // Re-arm the timeout guard with the remaining deadline
      startTimer(data.workflow_id, data.timeout_at, data.timeout_seconds);
    },
    [dispatch, startTimer]
  );

  useEffect(() => {
    if (!currentSocket) return;

    currentSocket.on('workflow_progress_restore', handler);

    return () => {
      currentSocket.off('workflow_progress_restore', handler);
    };
  }, [currentSocket, handler]);
};
